import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Circle, FileText, Play, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { MusicIcon, VisualArtIcon, WritingIcon, OtherIcon } from '../icons';
import { Button } from '../ui';
import { ProjectDto } from '../../services/projects';
import './SubProjectMiniCard.css';

interface SubProjectMiniCardProps {
  project: ProjectDto;
  onClick?: (project: ProjectDto) => void;
  onWorkOnIt?: (project: ProjectDto) => void;
  isPiece?: boolean; // Use Piece styling (teal accent)
  expandable?: boolean; // Enable expand/collapse behavior
}

const getProjectIcon = (type: string, size = 14) => {
  switch (type.toLowerCase()) {
    case 'music':
      return <MusicIcon size={size} />;
    case 'visualart':
    case 'visual_art':
      return <VisualArtIcon size={size} />;
    case 'writing':
      return <WritingIcon size={size} />;
    default:
      return <OtherIcon size={size} />;
  }
};

const getTypeClass = (type: string): string => {
  const normalizedType = type.toLowerCase().replace('_', '');
  return `sub-project-mini-card__icon--${normalizedType}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

export const SubProjectMiniCard: React.FC<SubProjectMiniCardProps> = ({
  project,
  onClick,
  onWorkOnIt,
  isPiece = false,
  expandable = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const isCompleted = project.status === 'Completed';

  const cardClasses = [
    'sub-project-mini-card',
    isCompleted && 'sub-project-mini-card--completed',
    isPiece && 'sub-project-mini-card--piece',
    isExpanded && 'sub-project-mini-card--expanded',
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (expandable) {
      setIsExpanded(!isExpanded);
    } else {
      onClick?.(project);
    }
  };

  const handleWorkOnIt = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onWorkOnIt) {
      onWorkOnIt(project);
    } else {
      onClick?.(project);
    }
  };

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="sub-project-mini-card__main">
        <div className={`sub-project-mini-card__icon ${isPiece ? 'sub-project-mini-card__icon--piece' : getTypeClass(project.projectType)}`}>
          {isPiece ? <FileText size={14} /> : getProjectIcon(project.projectType)}
        </div>
        <div className="sub-project-mini-card__content">
          <span className="sub-project-mini-card__title">{project.title}</span>
          <span className="sub-project-mini-card__stage">{project.currentStage}</span>
        </div>
        <div className="sub-project-mini-card__status">
          {isCompleted ? (
            <CheckCircle size={16} className="sub-project-mini-card__status-icon--completed" />
          ) : (
            <Circle size={16} className="sub-project-mini-card__status-icon--pending" />
          )}
        </div>
        {expandable && (
          <div className="sub-project-mini-card__expand-icon">
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        )}
      </div>

      {/* Expanded content */}
      {expandable && isExpanded && (
        <div className="sub-project-mini-card__details">
          {project.description && (
            <p className="sub-project-mini-card__description">{project.description}</p>
          )}

          <div className="sub-project-mini-card__meta">
            {project.createdAt && (
              <span className="sub-project-mini-card__meta-item">
                <Calendar size={12} />
                {formatDate(project.createdAt)}
              </span>
            )}
            {project.targetCompletionDate && (
              <span className="sub-project-mini-card__meta-item sub-project-mini-card__meta-item--target">
                Target: {formatDate(project.targetCompletionDate)}
              </span>
            )}
          </div>

          <Button
            variant="primary"
            size="sm"
            className="sub-project-mini-card__work-btn"
            onClick={handleWorkOnIt}
          >
            <Play size={14} />
            {t('works.pieces.workOnIt')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SubProjectMiniCard;
